import { Icon } from '@iconify/react'
import {
  Button,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@nextui-org/react'
import { Editor } from '@tiptap/core'
import { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import api from 'src/lib/api'

interface IAddImageModalProps {
  onOpenChangeMediaModal: () => void
  isMediaModalOpen: boolean
  imageToEdit: any
  setImageToEdit: (value: any) => void
  editor: Editor
}

export default function AddImageModal({
  onOpenChangeMediaModal,
  isMediaModalOpen,
  imageToEdit,
  setImageToEdit,
  editor,
}: IAddImageModalProps) {
  const [loadingError, setLoadingError] = useState<boolean>(false)
  const [loadingImage, setLoadingImage] = useState<boolean>(false)
  const imageUrlInput = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [uploading, setUploading] = useState<boolean>(false)
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      setPreviewUrl(URL.createObjectURL(file))
      setUploading(true)
      setLoadingImage(true)
      const formData = new FormData()
      formData.append('file', file)

      const res = await api.post('/powerdocs/attachement', formData)
      if (res.data?.url) {
        if (imageToEdit?.current) {
          imageToEdit.current.src = res.data.url
          imageToEdit.current.alt = res.data.url
          setImageToEdit(null)
        } else {
          // If valid, execute the editor commands
          editor?.commands.setMedia({
            src: res.data.url,
            'media-type': 'img',
            alt: '',
            title: 'Something',
            width: '800',
            height: '400',
          })
          if (imageUrlInput.current) imageUrlInput.current.value = ''
        }
      }
      setUploading(false)
      setPreviewUrl('')
      setLoadingImage(false)
      setImageToEdit(null)
    }
  }, [])
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    multiple: false,
  })
  return (
    <Modal isOpen={isMediaModalOpen} onOpenChange={onOpenChangeMediaModal} size={'4xl'}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Image</ModalHeader>
            <ModalBody>
              <div className={'flex flex-col gap-4 '}>
                <div className={'flex flex-row justify-between gap-10'}>
                  <Input
                    autoFocus
                    ref={imageUrlInput}
                    variant="faded"
                    labelPlacement="inside"
                    label={'Image link'}
                    placeholder="Enter your media link: e.g https://powerdocs.so/media/content.png"
                    className="w-full"
                    errorMessage={'Invalid image url'}
                    onChange={() => setLoadingError(false)}
                    isInvalid={loadingError}
                  />
                </div>
                <span className={'text-xs italic text-default-400'}>
                  Tip: You can make your proposal stand out by inserting rich content to visualize ideas to your
                  clients.
                </span>
                <Divider />
                <div
                  {...getRootProps({
                    className: 'flex flex-col gap-2 mt-2 bg-default-100 rounded-xl justify-center items-center',
                  })}
                >
                  <input {...getInputProps()} />
                  {uploading ? (
                    <div className={'flex flex-col justify-center items-center gap-4 p-4 w-full'}>
                      <div className={'bg-white flex rounded flex-row p-2 relative'} key={`file_${previewUrl}`}>
                        <img src={previewUrl} width={120} height={120} className="rounded" />
                        <Spinner size={'md'} color="secondary" className="absolute bottom-[-12px] right-[-12px]" />
                      </div>
                    </div>
                  ) : (
                    <div className={'flex flex-col justify-center items-center p-4'}>
                      <div className={'bg-white p-2 rounded-full mb-2'}>
                        <Icon icon={'lucide:cloud-upload'} />
                      </div>
                      <p className={'font-regular text-sm'}>
                        <span className={'pr-1 font-medium text-blue-400 cursor-pointer'} onClick={open}>
                          Click to upload or drag and drop
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                isDisabled={loadingImage || uploading}
                onPress={() => {
                  if (imageUrlInput.current) {
                    setLoadingError(false)
                    setLoadingImage(true)
                    const imageUrl = imageUrlInput.current.value

                    if (!imageUrl) {
                      console.error('Image URL is empty.')
                      setLoadingImage(false)
                      return
                    }

                    // Validate the image URL
                    const img = new Image()
                    img.onload = () => {
                      if (imageToEdit?.current) {
                        imageToEdit.current.src = imageUrl
                        imageToEdit.current.alt = imageUrl
                        setImageToEdit(null)
                      } else {
                        // If valid, execute the editor commands
                        editor?.commands.setMedia({
                          src: imageUrl,
                          'media-type': 'img',
                          alt: '',
                          title: 'Something',
                          width: '800',
                          height: '400',
                        })
                        if (imageUrlInput.current) imageUrlInput.current.value = ''
                      }
                      setLoadingImage(false)
                      onClose()
                    }

                    img.onerror = () => {
                      // If invalid, show an error
                      console.error('Invalid image URL.')
                      setLoadingError(true)
                      setLoadingImage(false)
                    }

                    img.src = imageUrl
                  }
                }}
              >
                {loadingImage ? (
                  <>
                    {'Validating image'} <Spinner size={'sm'} />
                  </>
                ) : (
                  'Insert Image'
                )}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
